import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
  scenarioService,
  templateService,
  performanceService,
} from "../services/scenarioService";
import { logger } from "../services/logging";
import {
  Scenario,
  CreateScenarioDto,
  UpdateScenarioDto,
  ScenarioFilterDto,
  ScenarioTemplate,
  CreateTemplateInput,
  UpdateTemplateInput,
  ScenarioPerformance,
  PerformanceMetrics,
  ScenarioStats,
  TemplateCategory,
  PaginatedResponse,
} from "../types/scenario";

// Hook for managing scenarios
export const useScenarios = (filter: ScenarioFilterDto = {}) => {
  // Memoize the filter to prevent unnecessary re-renders
  const memoizedFilter = useMemo(() => filter, [JSON.stringify(filter)]);

  // Track if this is the initial mount
  const isInitialMount = useRef(true);

  const [scenarios, setScenarios] = useState<Scenario[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalPages, setTotalPages] = useState(0);

  const fetchScenarios = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await scenarioService.getScenarios({
        ...memoizedFilter,
        page,
        limit,
      });

      // Debug logging
      logger.debug(
        "GraphQL response",
        { method: "fetchScenarios" },
        { response },
      );

      if (!response || !response.items) {
        logger.error(
          "Invalid response structure",
          { method: "fetchScenarios" },
          { response },
        );
        setError("Invalid response structure from server");
        setScenarios([]);
        return;
      }

      setScenarios(response.items);
      setTotal(response.total);
      setTotalPages(response.totalPages);
      // Don't update page and limit from response to avoid infinite loops
    } catch (err) {
      logger.error(
        "Error fetching scenarios",
        { method: "fetchScenarios" },
        err,
      );
      setError(
        err instanceof Error ? err.message : "Failed to fetch scenarios",
      );
      setScenarios([]);
    } finally {
      setLoading(false);
    }
  }, [memoizedFilter, page, limit]);

  const createScenario = useCallback(async (data: CreateScenarioDto) => {
    try {
      setError(null);
      const newScenario = await scenarioService.createScenario(data);
      setScenarios((prev) => [newScenario, ...prev]);
      return newScenario;
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to create scenario",
      );
      throw err;
    }
  }, []);

  const updateScenario = useCallback(
    async (id: string, data: UpdateScenarioDto) => {
      try {
        setError(null);
        const updatedScenario = await scenarioService.updateScenario(id, data);
        setScenarios((prev) =>
          prev.map((s) => (s.id === id ? updatedScenario : s)),
        );
        return updatedScenario;
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "Failed to update scenario",
        );
        throw err;
      }
    },
    [],
  );

  const deleteScenario = useCallback(async (id: string) => {
    try {
      setError(null);
      await scenarioService.deleteScenario(id);
      setScenarios((prev) => prev.filter((s) => s.id !== id));
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to delete scenario",
      );
      throw err;
    }
  }, []);

  const activateScenario = useCallback(async (id: string) => {
    try {
      setError(null);
      const updatedScenario = await scenarioService.activateScenario(id);
      setScenarios((prev) =>
        prev.map((s) => (s.id === id ? updatedScenario : s)),
      );
      return updatedScenario;
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to activate scenario",
      );
      throw err;
    }
  }, []);

  const deactivateScenario = useCallback(async (id: string) => {
    try {
      setError(null);
      const updatedScenario = await scenarioService.deactivateScenario(id);
      setScenarios((prev) =>
        prev.map((s) => (s.id === id ? updatedScenario : s)),
      );
      return updatedScenario;
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to deactivate scenario",
      );
      throw err;
    }
  }, []);

  const cloneScenario = useCallback(async (id: string, name?: string) => {
    try {
      setError(null);
      const clonedScenario = await scenarioService.cloneScenario(id, name);
      setScenarios((prev) => [clonedScenario, ...prev]);
      return clonedScenario;
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to clone scenario");
      throw err;
    }
  }, []);

  const setPageNumber = useCallback((newPage: number) => {
    setPage(newPage);
  }, []);

  const setLimitNumber = useCallback((newLimit: number) => {
    setLimit(newLimit);
    setPage(1);
  }, []);

  const refreshScenarios = useCallback(() => {
    fetchScenarios();
  }, [fetchScenarios]);

  // Initial fetch
  useEffect(() => {
    fetchScenarios();
  }, []); // Empty dependency array for initial fetch only

  // Fetch when filter, page, or limit changes
  useEffect(() => {
    // Skip the initial mount to prevent unnecessary API calls
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    // Only fetch if we have valid page and limit values
    if (page > 0 && limit > 0) {
      // Call the API directly to avoid dependency loops
      const fetchData = async () => {
        try {
          setLoading(true);
          setError(null);
          const response = await scenarioService.getScenarios({
            ...memoizedFilter,
            page,
            limit,
          });

          if (!response || !response.items) {
            setError("Invalid response structure from server");
            setScenarios([]);
            return;
          }

          setScenarios(response.items);
          setTotal(response.total);
          setTotalPages(response.totalPages);
        } catch (err) {
          logger.error(
            "Error fetching scenarios",
            { method: "fetchScenarios" },
            err,
          );
          setError(
            err instanceof Error ? err.message : "Failed to fetch scenarios",
          );
          setScenarios([]);
        } finally {
          setLoading(false);
        }
      };

      fetchData();
    }
  }, [page, limit, memoizedFilter]); // Include memoizedFilter for filter changes

  return {
    scenarios,
    loading,
    error,
    pagination: { total, page, limit, totalPages },
    fetchScenarios,
    createScenario,
    updateScenario,
    deleteScenario,
    activateScenario,
    deactivateScenario,
    cloneScenario,
    setPage: setPageNumber,
    setLimit: setLimitNumber,
    refreshScenarios,
  };
};

// Hook for managing a single scenario
export const useScenario = (id: string | null) => {
  const [scenario, setScenario] = useState<Scenario | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchScenario = useCallback(async () => {
    if (!id) return;

    try {
      setLoading(true);
      setError(null);
      const data = await scenarioService.getScenario(id);
      setScenario(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to fetch scenario");
    } finally {
      setLoading(false);
    }
  }, [id]);

  const updateScenario = useCallback(
    async (data: UpdateScenarioDto) => {
      if (!id) return;

      try {
        setError(null);
        const updatedScenario = await scenarioService.updateScenario(id, data);
        setScenario(updatedScenario);
        return updatedScenario;
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "Failed to update scenario",
        );
        throw err;
      }
    },
    [id],
  );

  useEffect(() => {
    fetchScenario();
  }, [fetchScenario]);

  return {
    scenario,
    loading,
    error,
    fetchScenario,
    updateScenario,
  };
};

// Hook for managing active scenarios
export const useActiveScenarios = () => {
  const [scenarios, setScenarios] = useState<Scenario[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchActiveScenarios = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await scenarioService.getActiveScenarios();
      setScenarios(data);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to fetch active scenarios",
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchActiveScenarios();
  }, []); // Only fetch once on mount

  return {
    scenarios,
    loading,
    error,
    fetchActiveScenarios,
  };
};

// Hook for managing scenario statistics
export const useScenarioStats = () => {
  const [stats, setStats] = useState<ScenarioStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchStats = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await scenarioService.getScenarioStats();
      setStats(data);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Failed to fetch scenario statistics",
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();
  }, []); // Only fetch once on mount

  return {
    stats,
    loading,
    error,
    fetchStats,
  };
};

// Hook for managing templates
export const useTemplates = (
  filters: {
    category?: string;
    riskLevel?: string;
    timeFrame?: string;
    difficulty?: string;
    tags?: string;
    author?: string;
  } = {},
) => {
  const [templates, setTemplates] = useState<ScenarioTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchTemplates = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await templateService.getTemplates(filters);
      setTemplates(data);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to fetch templates",
      );
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const createTemplate = useCallback(async (data: CreateTemplateInput) => {
    try {
      setError(null);
      const newTemplate = await templateService.createTemplate(data);
      setTemplates((prev) => [newTemplate, ...prev]);
      return newTemplate;
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to create template",
      );
      throw err;
    }
  }, []);

  const updateTemplate = useCallback(
    async (id: string, data: UpdateTemplateInput) => {
      try {
        setError(null);
        const updatedTemplate = await templateService.updateTemplate(id, data);
        setTemplates((prev) =>
          prev.map((t) => (t.id === id ? updatedTemplate : t)),
        );
        return updatedTemplate;
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "Failed to update template",
        );
        throw err;
      }
    },
    [],
  );

  const deleteTemplate = useCallback(async (id: string) => {
    try {
      setError(null);
      await templateService.deleteTemplate(id);
      setTemplates((prev) => prev.filter((t) => t.id !== id));
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to delete template",
      );
      throw err;
    }
  }, []);

  useEffect(() => {
    fetchTemplates();
  }, []); // Only fetch once on mount

  return {
    templates,
    loading,
    error,
    fetchTemplates,
    createTemplate,
    updateTemplate,
    deleteTemplate,
  };
};

// Hook for managing template categories
export const useTemplateCategories = () => {
  const [categories, setCategories] = useState<TemplateCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchCategories = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await templateService.getTemplateCategories();
      setCategories(data);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Failed to fetch template categories",
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCategories();
  }, []); // Only fetch once on mount

  return {
    categories,
    loading,
    error,
    fetchCategories,
  };
};

// Hook for managing popular templates
export const usePopularTemplates = (limit: number = 10) => {
  const [templates, setTemplates] = useState<ScenarioTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchPopularTemplates = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await templateService.getPopularTemplates(limit);
      setTemplates(data);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Failed to fetch popular templates",
      );
    } finally {
      setLoading(false);
    }
  }, [limit]);

  useEffect(() => {
    fetchPopularTemplates();
  }, []); // Only fetch once on mount

  return {
    templates,
    loading,
    error,
    fetchPopularTemplates,
  };
};

// Hook for managing scenario performance
export const useScenarioPerformance = (scenarioId: string | null) => {
  const [performance, setPerformance] = useState<PerformanceMetrics | null>(
    null,
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchPerformance = useCallback(
    async (startDate?: Date, endDate?: Date) => {
      if (!scenarioId) return;

      try {
        setLoading(true);
        setError(null);
        const data = await performanceService.getPerformance(
          scenarioId,
          startDate,
          endDate,
        );
        setPerformance(data);
      } catch (err) {
        setError(
          err instanceof Error
            ? err.message
            : "Failed to fetch performance data",
        );
      } finally {
        setLoading(false);
      }
    },
    [scenarioId],
  );

  useEffect(() => {
    if (scenarioId) {
      fetchPerformance();
    }
  }, [scenarioId]); // Remove fetchPerformance dependency

  return {
    performance,
    loading,
    error,
    fetchPerformance,
  };
};

// Hook for managing performance reports
export const usePerformanceReport = (scenarioId: string | null) => {
  const [report, setReport] = useState<ScenarioPerformance | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const generateReport = useCallback(
    async (startDate?: Date, endDate?: Date) => {
      if (!scenarioId) return;

      try {
        setLoading(true);
        setError(null);
        const data = await performanceService.generateReport(
          scenarioId,
          startDate,
          endDate,
        );
        setReport(data);
      } catch (err) {
        setError(
          err instanceof Error
            ? err.message
            : "Failed to generate performance report",
        );
      } finally {
        setLoading(false);
      }
    },
    [scenarioId],
  );

  return {
    report,
    loading,
    error,
    generateReport,
  };
};

// Hook for managing performance history
export const usePerformanceHistory = (
  scenarioId: string | null,
  limit: number = 100,
) => {
  const [history, setHistory] = useState<ScenarioPerformance[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchHistory = useCallback(async () => {
    if (!scenarioId) return;

    try {
      setLoading(true);
      setError(null);
      const data = await performanceService.getPerformanceHistory(
        scenarioId,
        limit,
      );
      setHistory(data);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Failed to fetch performance history",
      );
    } finally {
      setLoading(false);
    }
  }, [scenarioId, limit]);

  useEffect(() => {
    if (scenarioId) {
      fetchHistory();
    }
  }, [scenarioId]); // Remove fetchHistory dependency

  return {
    history,
    loading,
    error,
    fetchHistory,
  };
};

// Hook for comparing scenarios
export const useScenarioComparison = () => {
  const [comparison, setComparison] = useState<ScenarioPerformance[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const compareScenarios = useCallback(
    async (scenarioIds: string[], startDate?: Date, endDate?: Date) => {
      try {
        setLoading(true);
        setError(null);
        const data = await performanceService.compareScenarios(
          scenarioIds,
          startDate,
          endDate,
        );
        setComparison(data);
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "Failed to compare scenarios",
        );
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return {
    comparison,
    loading,
    error,
    compareScenarios,
  };
};
